import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { auditListSchema } from '@competa/shared';
import { apiRequest } from '../../api/client';
import { TextField } from '../../components/TextField';
import { errorMessage } from '../../i18n/errors';
import styles from './Admin.module.css';

/**
 * Audit-Protokoll: sicherheitsrelevante Aktionen (Benutzerverwaltung, fehlgeschlagene Anmeldungen,
 * Passwortwechsel, Löschen und Abschließen von Wettbewerben, Imports), filterbar nach Aktion und Benutzer.
 */
export function AuditPage() {
  const { t, i18n } = useTranslation();
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');

  const audit = useQuery({
    queryKey: ['admin', 'audit', action, actor],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '100' });
      if (action.trim()) params.set('action', action.trim());
      if (actor.trim()) params.set('actor', actor.trim());
      return apiRequest('GET', `/admin/audit?${params}`, { schema: auditListSchema });
    },
  });

  return (
    <div>
      <h2>{t('admin.audit.title')}</h2>
      <p className={styles.note}>{t('admin.audit.intro')}</p>
      <div className={styles.toolbar}>
        <TextField
          label={t('admin.audit.filterAction')}
          hint={t('admin.audit.filterActionHint')}
          value={action}
          onChange={(event) => setAction(event.target.value)}
        />
        <TextField
          label={t('admin.audit.filterActor')}
          value={actor}
          onChange={(event) => setActor(event.target.value)}
        />
      </div>
      {audit.isPending && <p role="status">{t('common.loading')}</p>}
      {audit.isError && (
        <p role="alert" className={styles.error}>
          {errorMessage(t, audit.error)}
        </p>
      )}
      {audit.data && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('admin.audit.when')}</th>
                <th>{t('admin.audit.actor')}</th>
                <th>{t('admin.audit.action')}</th>
                <th>{t('admin.audit.detail')}</th>
                <th>{t('admin.log.ip')}</th>
              </tr>
            </thead>
            <tbody>
              {audit.data.entries.length === 0 && (
                <tr>
                  <td colSpan={5}>{t('admin.log.empty')}</td>
                </tr>
              )}
              {audit.data.entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{new Date(entry.createdAt).toLocaleString(i18n.language)}</td>
                  <td>{entry.actor ?? '–'}</td>
                  <td>
                    {t(`admin.audit.actions.${entry.action}`, { defaultValue: entry.action })}
                  </td>
                  <td>
                    {Object.entries(entry.detail)
                      .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
                      .join(', ') || '–'}
                  </td>
                  <td>{entry.ip ?? '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
